/**
 * Strategic value of acquiring a competitor — pure helper.
 *
 * 02/06/2026 — Companion to the tier-escalation refactor of M&A
 * vehicles. Now that late-game competitors carry tier 4-5 fleets, the
 * apprenant needs a single decision-grade KPI to answer the question :
 * « quel concurrent vaut le rachat ? ».
 *
 * The strategic value is a 0–100 composite blending FOUR dimensions :
 *
 *   • Fleet tier mix     (40 %) — proxied via `analysis.fleet`, scaled
 *                                  by the era's expected tier ladder.
 *                                  Heavier weight because tier-4+
 *                                  vehicles unlock high-margin contracts.
 *   • Specialties        (25 %) — count of declared specialties
 *                                  (Frigorifique, ADR, BTP, Export EU, Vrac,
 *                                  Messagerie). More specialties = more
 *                                  contract diversity post-acquisition.
 *   • Annual revenue     (20 %) — `analysis.ca` mapped to a log-scale,
 *                                  rewards established players over micro
 *                                  competitors.
 *   • Geographic reach   (15 %) — number of declared depots (proxied via
 *                                  `competitor.depots?.length`, defaults
 *                                  to 1 = single HQ).
 *
 * Output : a tier label ('strategic' / 'attractive' / 'limited' /
 * 'marginal') + a 0-100 score + per-dimension scores so the UI can
 * display a tooltip explaining WHY a competitor is rated as such.
 *
 * Pure : no side-effects, no store access.
 */

/**
 * @param {Object} competitor - the raw COMPETITORS entry (for specialties, depots)
 * @param {Object} analysis - the result of analyzeCompetitor()
 * @param {Object} ctx
 * @param {number} ctx.yearsPlayed - Math.floor(totalDays / 360) ; used to
 *   normalise the expected tier mix (year 1 baseline vs year 5+ expected
 *   tier 4-5 fleet).
 * @returns {{
 *   score: number,         // 0-100 composite
 *   tier: 'strategic'|'attractive'|'limited'|'marginal',
 *   label: string,         // FR display label
 *   tone: 'emerald'|'sky'|'amber'|'rose',
 *   breakdown: { fleet: number, specialties: number, revenue: number, reach: number },
 * }}
 */
export function computeStrategicAcquisitionValue(competitor, analysis, ctx = {}) {
  if (!competitor || !analysis || analysis.locked) {
    return null;
  }
  const yearsPlayed = Math.max(0, Math.floor(ctx.yearsPlayed || 0));

  // ── 1. Fleet score (0-100) ──────────────────────────────────────
  // Use fleet size as a proxy (we don't have the per-vehicle tier list
  // until acquisition completes), modulated by the expected average tier
  // for the era. Year 1 baseline = tier 2.0 avg ; year 5+ = tier 4.0 avg.
  const expectedAvgTier = Math.min(4.5, 2.0 + yearsPlayed * 0.5);
  const fleetSize = analysis.fleet || 0;
  // 8 vehicles + max expected tier ≈ saturates the score at ~95.
  const fleetScore = Math.min(100, (fleetSize / 8) * 50 + (expectedAvgTier / 4.5) * 50);

  // ── 2. Specialty score (0-100) ──────────────────────────────────
  // 0 specialties = 0 / 1 = 35 / 2 = 65 / 3+ = 100 (sweet spot).
  const specCount = (competitor.specialties || []).length;
  const specialtyScore = Math.min(100, specCount * 35);

  // ── 3. Revenue score (0-100, log-scaled) ────────────────────────
  // log10(ca / 5000) × 50 — ca 5k = 0 / 50k = 50 / 500k = 100.
  const ca = Math.max(1, analysis.ca || 0);
  const revenueScore = Math.max(0, Math.min(100, Math.log10(ca / 5000 + 1) * 50));

  // ── 4. Reach score (0-100) ──────────────────────────────────────
  // Each declared depot adds 20 points (1 = HQ only, 5+ = national).
  const depotCount = Math.max(1, (competitor.depots || []).length || 1);
  const reachScore = Math.min(100, depotCount * 20);

  // ── Weighted composite ──────────────────────────────────────────
  const score = Math.round(
    fleetScore * 0.40
    + specialtyScore * 0.25
    + revenueScore * 0.20
    + reachScore * 0.15,
  );

  // Tier mapping.
  let tier = 'marginal';
  let label = 'Intérêt marginal';
  let tone = 'rose';
  if (score >= 75) { tier = 'strategic'; label = 'Cible stratégique'; tone = 'emerald'; }
  else if (score >= 55) { tier = 'attractive'; label = 'Cible attractive'; tone = 'sky'; }
  else if (score >= 35) { tier = 'limited'; label = 'Intérêt limité'; tone = 'amber'; }

  return {
    score,
    tier,
    label,
    tone,
    breakdown: {
      fleet: Math.round(fleetScore),
      specialties: Math.round(specialtyScore),
      revenue: Math.round(revenueScore),
      reach: Math.round(reachScore),
    },
  };
}
